// src/lib/db/ids.ts
//
// ═══════════════════════════════════════════════════════════════════════════
//  FROZEN CONTRACT — id minting. SPEC §1.1 ("IDs — UUIDv7, ~14 inlined lines
//  in src/lib/db/ids.ts") and §3.3.
//
//  Two kinds of id, and the difference is the whole point:
//
//  1. NATURALLY-KEYED FACTS get a DETERMINISTIC id. "Fajr on 2026-09-08" is
//     one fact in the world. If the phone and the laptop each record it, they
//     must produce THE SAME ROW — then it is not a conflict, it cannot trip an
//     index on import, and `mergeRow` resolves it by the ordinary LWW rule
//     instead of leaving two rows the user has to look at. `prayer_log`,
//     `day_state`, `fast_log`, `memorization`, `note_tags`, `drafts` and
//     `positions` are all in this class.
//
//  2. GENUINELY INDEPENDENT CREATIONS get a UUIDv7. Two notes written on two
//     devices about the same ayah are two different notes, and any scheme that
//     made them collide would silently destroy one of them.
//
//  UUIDv7 and not `crypto.randomUUID()`: v4 is random-ordered, which destroys
//  the locality of the `[updated_at+id]` delta scan that `SyncPort.since()` is
//  built on. v7 is time-ordered, so a scan of "everything since cursor X" is a
//  contiguous range instead of a full-table sort. Not worth a package.
// ═══════════════════════════════════════════════════════════════════════════

import type { AyahId, Prayer } from './schema';

const HEX: readonly string[] = Array.from({ length: 256 }, (_, i) =>
	i.toString(16).padStart(2, '0')
);

/**
 * RFC 9562 UUIDv7: 48-bit big-endian Unix milliseconds, then version 7, then
 * 74 bits of randomness.
 *
 * Deliberately NOT monotonic within a millisecond. Two ids minted in the same
 * millisecond sort arbitrarily against each other, which is fine: the delta
 * cursor is `[updated_at+id]`, so ties in `updated_at` are broken by the id
 * consistently for every reader, and that is all the ordering has to promise.
 * A monotonic counter would need shared mutable state across tabs, and a
 * BroadcastChannel is not worth buying an ordering nobody consumes.
 */
export function uuidv7(now: number = Date.now()): string {
	const rnd = crypto.getRandomValues(new Uint8Array(10));
	const b = new Array<number>(16);

	// 48-bit timestamp. Written with % and Math.floor, never with >>>, because
	// bitwise operators truncate to 32 bits and Date.now() has been past 2^32
	// milliseconds since 1970 + 49 days.
	const ts = Math.max(0, Math.floor(now));
	b[0] = Math.floor(ts / 1099511627776) % 256; // 2^40
	b[1] = Math.floor(ts / 4294967296) % 256; //     2^32
	b[2] = Math.floor(ts / 16777216) % 256; //       2^24
	b[3] = Math.floor(ts / 65536) % 256; //          2^16
	b[4] = Math.floor(ts / 256) % 256; //            2^8
	b[5] = ts % 256;

	for (let i = 0; i < 10; i++) b[6 + i] = rnd[i] ?? 0;
	b[6] = ((b[6] ?? 0) & 0x0f) | 0x70; // version 7
	b[8] = ((b[8] ?? 0) & 0x3f) | 0x80; // variant 10xx

	let out = '';
	for (let i = 0; i < 16; i++) {
		out += HEX[b[i] ?? 0];
		if (i === 3 || i === 5 || i === 7 || i === 9) out += '-';
	}
	return out;
}

/** Shape check only — never a trust boundary. Import coercion (§3.7 step 6)
 *  uses it to decide whether an id is a usable IndexedDB key, and a foreign
 *  but well-formed id from a future version must still be accepted. */
export function isUuidv7(value: unknown): boolean {
	return (
		typeof value === 'string' &&
		/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value)
	);
}

/** Milliseconds encoded in a UUIDv7, or `null` if it is not one. Used by the
 *  Repair screen to date a row whose `created_at` was corrupted. */
export function uuidv7Time(id: string): number | null {
	if (!isUuidv7(id)) return null;
	return Number.parseInt(id.slice(0, 8) + id.slice(9, 13), 16);
}

/* ═══════════ DETERMINISTIC IDS ═══════════
 * Every one of these is a pure function of the fact it names. Changing any of
 * them is a migration on every device, so they are written once, here, and
 * nothing else in the codebase builds one of these strings by hand.
 */

/** `prayer_log`. `day` is a LOCAL civil date (`YYYY-MM-DD`), never a UTC
 *  timestamp: "did I pray fajr today" is a question about the user's calendar,
 *  and a UTC day boundary answers it wrongly for half the planet. */
export function prayerLogId(day: string, prayer: Prayer): string {
	return `${day}:${prayer}`;
}

/** `day_state`. One row per civil day. */
export function dayStateId(day: string): string {
	return day;
}

/** `fast_log`. One row per civil day. */
export function fastLogId(day: string): string {
	return day;
}

/** `memorization`. Frozen in v1 although the UI lands in v2 — ids must be
 *  stable BEFORE anything writes, so freezing now costs nothing. */
export function memoId(ayahId: AyahId): string {
	return `memo:${ayahId}`;
}

/** `note_tags`. Set membership as its own row: two devices adding the same tag
 *  to the same note produce the same row, so tag sets merge by UNION and can
 *  never conflict. */
export function noteTagId(noteId: string, tagId: string): string {
	return `${noteId}:${tagId}`;
}

/** `drafts`. Pre-commit crash recovery, keyed by anchor ref + device. */
export function draftId(ref: string, deviceId: string): string {
	return `${ref}:${deviceId}`;
}

/** `positions`. Per (surface, device) and deliberately NOT merged across
 *  devices — "where I am on my phone" and "where I am on my laptop" are
 *  different facts. */
export function positionId(surface: 'quran' | 'azkar', deviceId: string): string {
	return `${surface}:${deviceId}`;
}
